/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CoreModule
 */

import $ from 'jquery';
import { throttle } from 'underscore';
import { L10nService } from '@vmw/ngx-vip';
import { StateParams, StateService } from '@uirouter/core';

import {
    AppStateHandler,
    UserSessionEventListenersService,
    USER_SESSION_EVENT_LISTENERS_SERVICE_TOKEN,
} from 'ajs/modules/core/services';

import * as globalL10n from 'global-l10n';
import * as schemaL10n from 'ajs/js/services/schema.l10n';
import * as l10n from './core.l10n';

interface IAviRootscopeService extends ng.IRootScopeService {
    $state?: StateService;
    $stateParams?: StateParams;
}

const { ENGLISH: globalDictionary } = globalL10n;
const resizeEvent = 'resize';
const repaintEvent = 'repaint';
const $repaintViewPortEvent = '$repaintViewport';

const { ENGLISH } = l10n;
const { ENGLISH: schemaDict } = schemaL10n;

/**
 * @description
 * Run block for ajs core module.
 *
 * @author Alex Malitsky, Aravindh Nagarajan
 */
export function initCoreModule(
    $rootScope: IAviRootscopeService,
    $state: StateService,
    $stateParams: StateParams,
    $window: ng.IWindowService,
    activateAviModalJQueryPlugin: ($: JQueryStatic) => void,
    userSessionEventListenersService: UserSessionEventListenersService,
    appStateHandler: AppStateHandler,
    l10nService: L10nService,
    logErrorToController: OnErrorEventHandlerNonNull,
): void {
    activateAviModalJQueryPlugin($);
    userSessionEventListenersService.setListeners();
    appStateHandler.init();
    l10nService.registerSourceBundles(ENGLISH);
    l10nService.registerSourceBundles(globalDictionary);
    l10nService.registerSourceBundles(schemaDict);

    /** @deprecated */
    $rootScope.$state = $state;

    /** @deprecated */
    $rootScope.$stateParams = $stateParams;

    // add single window listener to
    $($window).on(
        resizeEvent,
        throttle(() => {
            $rootScope.$broadcast($repaintViewPortEvent);
            $rootScope.$broadcast(repaintEvent);
        }, 100),
    );

    $window.onerror = logErrorToController;
}

initCoreModule.$inject = [
    '$rootScope',
    '$state',
    '$stateParams',
    '$window',
    'activateAviModalJQueryPlugin',
    USER_SESSION_EVENT_LISTENERS_SERVICE_TOKEN,
    'appStateHandler',
    'l10nService',
    'logErrorToController',
];
